//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import BatchTransactionsModel from './batchTransactions.model';

/**
 * @service BatchTransactionsService
 * @description Service for managing batch transaction data operations (feed and treatment logs).
 * Provides HTTP methods for querying and creating batch feed logs and treatment logs.
 * Extends base Service class with generic type BatchTransactionsModel.
 *
 * @feature 1. Query all feed batch transactions for farm site and section
 * @feature 2. Query latest/single feed batch transaction with pagination
 * @feature 3. Query all treatment batch transactions for farm site and section
 * @feature 4. Query latest/single treatment batch transaction with pagination
 * @feature 5. Create new treatment log records
 * @feature 6. Create new feed log records (batch)
 * @feature 7. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 8. Promise-based async operations for batch transaction management
 *
 * @architecture
 * Extends base Service<BatchTransactionsModel> providing REST API integration with backend.
 * Supports querying with batch_type parameter (Feed/Treatment) and pagination (pageSize, page).
 * Implements request tracking for cleanup and cancellation. Uses HttpClient for HTTP operations
 * with standard error handling via HttpErrorResponse.
 *
 * @service-endpoint /blubay/feedbatchentry/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 * @created 2025-01-19
 */
@Injectable({
  providedIn: 'root'
})
export class BatchTransactionsService extends Service<BatchTransactionsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path (/blubay/feedbatchentry/).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/feedbatchentry/');
  }

  /**
   * @method getAllBatchTransactionsByIds
   * @async
   * @description Fetch all feed batch transactions for a specific farm site and section with pagination
   *
   * Makes HTTP GET request to backend with query parameters for farm site ID, section ID,
   * batch type (Feed), and large page size (20000) to retrieve all records. Sorts by newest first.
   *
   * @param {string} businessId - Business ID (included in signature but not used in query)
   * @param {string} formSiteId - Farm site ID for filtering transactions
   * @param {string} sectionId - Section ID for filtering transactions
   *
   * @returns {Promise<Array<BatchTransactionsModel>>} Promise resolving to array of feed batch transactions
   *
   * @query-params
   * - farm_site_id: {formSiteId}
   * - section_id: {sectionId}
   * - batch_type: Feed
   * - pageSize: 20000
   * - order: new
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const feedBatches = await service.getAllBatchTransactionsByIds('biz123', 'site456', 'sec789');
   * console.log(feedBatches); // Array of feed batch transaction records
   */
  public getAllBatchTransactionsByIds(businessId: string, formSiteId: string, sectionId: string) {
    return new Promise<Array<BatchTransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BatchTransactionsModel>>(`${this.baseUrl}${this.serviceUrl}?farm_site_id=${formSiteId}&section_id=${sectionId}&batch_type=Feed&pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getLatestFeedBatchTransaction
   * @async
   * @description Fetch the most recent (latest) feed batch transaction for a specific farm site and section
   *
   * Makes HTTP GET request to backend with query parameters for farm site ID, section ID,
   * batch type (Feed), and pagination (page=0, pageSize=1) to retrieve only the latest record.
   *
   * @param {string} businessId - Business ID (included in signature but not used in query)
   * @param {string} formSiteId - Farm site ID for filtering transactions
   * @param {Number} sectionId - Section ID for filtering transactions
   *
   * @returns {Promise<Array<BatchTransactionsModel>>} Promise resolving to single-item array with latest feed batch
   *
   * @query-params
   * - farm_site_id: {formSiteId}
   * - section_id: {sectionId}
   * - batch_type: Feed
   * - order: new (newest first)
   * - page: 0
   * - pageSize: 1
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const latestFeedBatch = await service.getLatestFeedBatchTransaction('biz123', 'site456', 789);
   * console.log(latestFeedBatch[0]); // Latest feed batch record or empty if none
   */
  public getLatestFeedBatchTransaction(businessId: string, formSiteId: string, sectionId: Number) {
    return new Promise<Array<BatchTransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BatchTransactionsModel>>(`${this.baseUrl}${this.serviceUrl}?farm_site_id=${formSiteId}&section_id=${sectionId}&batch_type=Feed&order=new&page=0&pageSize=1`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }
  
  /**
   * @method getAllBatchTreatmentTransactionsByIds
   * @async
   * @description Fetch all treatment batch transactions for a specific farm site and section with pagination
   *
   * Makes HTTP GET request to backend with query parameters for farm site ID, section ID,
   * batch type (Treatment), and large page size (20000) to retrieve all records. Sorts by newest first.
   *
   * @param {string} businessId - Business ID (included in signature but not used in query)
   * @param {string} formSiteId - Farm site ID for filtering transactions
   * @param {string} sectionId - Section ID for filtering transactions
   *
   * @returns {Promise<Array<BatchTransactionsModel>>} Promise resolving to array of treatment batch transactions
   *
   * @query-params
   * - farm_site_id: {formSiteId}
   * - section_id: {sectionId}
   * - batch_type: Treatment
   * - pageSize: 20000
   * - order: new
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const treatmentBatches = await service.getAllBatchTreatmentTransactionsByIds('biz123', 'site456', 'sec789');
   * console.log(treatmentBatches); // Array of treatment batch transaction records
   */
  public getAllBatchTreatmentTransactionsByIds(businessId: string, formSiteId: string, sectionId: string) {
    return new Promise<Array<BatchTransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BatchTransactionsModel>>(`${this.baseUrl}${this.serviceUrl}?farm_site_id=${formSiteId}&section_id=${sectionId}&batch_type=Treatment&pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getLatestTreatmentBatchTransaction
   * @async
   * @description Fetch the most recent (latest) treatment batch transaction for a specific farm site and section
   *
   * Makes HTTP GET request to backend with query parameters for farm site ID, section ID,
   * batch type (Treatment), and pagination (page=0, pageSize=1) to retrieve only the latest record.
   *
   * @param {string} businessId - Business ID (included in signature but not used in query)
   * @param {string} formSiteId - Farm site ID for filtering transactions
   * @param {number} sectionId - Section ID for filtering transactions
   *
   * @returns {Promise<Array<BatchTransactionsModel>>} Promise resolving to single-item array with latest treatment batch
   *
   * @query-params
   * - farm_site_id: {formSiteId}
   * - section_id: {sectionId}
   * - batch_type: Treatment
   * - order: new (newest first)
   * - page: 0
   * - pageSize: 1
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const latestTreatmentBatch = await service.getLatestTreatmentBatchTransaction('biz123', 'site456', 789);
   * console.log(latestTreatmentBatch[0]); // Latest treatment batch record or empty if none
   */
  public getLatestTreatmentBatchTransaction(businessId: string, formSiteId: string, sectionId: number) {
    return new Promise<Array<BatchTransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BatchTransactionsModel>>(`${this.baseUrl}${this.serviceUrl}?farm_site_id=${formSiteId}&section_id=${sectionId}&batch_type=Treatment&order=new&page=0&pageSize=1`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addTreatmentLog
   * @async
   * @description Create a new treatment log record in the database
   *
   * Makes HTTP POST request to backend endpoint /blubay/feedbatchentry/addTreatmentLog
   * with treatment data to create new treatment log record. Wraps response in Promise for
   * async/await compatibility.
   *
   * @param {BatchTransactionsModel} treatments - Treatment log object with all required fields
   * @returns {Promise<BatchTransactionsModel>} Promise resolving to created treatment log with ID and metadata
   *
   * @request-body {BatchTransactionsModel} treatments - Treatment object containing:
   *   - batch_type: 'Treatment'
   *   - farm_site_id: ID of farm site
   *   - section_id: ID of section
   *   - treatment_date: Date of treatment
   *   - treatment_type: Type of treatment applied
   *   - dosage: Amount/dosage applied
   *   - notes: Additional treatment notes (optional)
   *
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   * - Creates new record in backend database
   *
   * @throws Rejects promise with HttpErrorResponse if request fails or validation fails
   * @example
   * const treatment = new BatchTransactionsModel();
   * treatment.batch_type = 'Treatment';
   * treatment.farm_site_id = 'site123';
   * treatment.treatment_type = 'Antibiotic';
   * const result = await service.addTreatmentLog(treatment);
   * console.log(result.id); // Newly created treatment log ID
   */
  public addTreatmentLog(treatments: BatchTransactionsModel) {
    return new Promise<BatchTransactionsModel>((resolve, reject) => {
      const request = this.httpClient.post<BatchTransactionsModel>(`${this.baseUrl}${this.serviceUrl}addTreatmentLog`, treatments, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addBatchFeedLogs
   * @async
   * @description Create new feed log record(s) in the database
   *
   * Makes HTTP POST request to backend endpoint /blubay/feedbatchentry/addFeedLogs
   * with feed log data to create new feed log record(s). Wraps response in Promise for
   * async/await compatibility.
   *
   * @param {BatchTransactionsModel} feedlogs - Feed log object with all required fields
   * @returns {Promise<BatchTransactionsModel>} Promise resolving to created feed log with ID and metadata
   *
   * @request-body {BatchTransactionsModel} feedlogs - Feed log object containing:
   *   - batch_type: 'Feed'
   *   - farm_site_id: ID of farm site
   *   - section_id: ID of section
   *   - feed_date: Date of feeding
   *   - feed_type: Type of feed (Pellet, Powder, etc.)
   *   - quantity: Amount of feed given
   *   - uom_id: Unit of measurement ID (grams, kilograms, etc.)
   *   - notes: Additional feeding notes (optional)
   *
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   * - Creates new record in backend database
   *
   * @throws Rejects promise with HttpErrorResponse if request fails or validation fails
   * @example
   * const feedLog = new BatchTransactionsModel();
   * feedLog.batch_type = 'Feed';
   * feedLog.farm_site_id = 'site123';
   * feedLog.feed_type = 'Pellet';
   * feedLog.quantity = 50;
   * const result = await service.addBatchFeedLogs(feedLog);
   * console.log(result.id); // Newly created feed log ID
   */
  public addBatchFeedLogs(feedlogs: BatchTransactionsModel) {
    return new Promise<BatchTransactionsModel>((resolve, reject) => {
      const request = this.httpClient.post<BatchTransactionsModel>(`${this.baseUrl}${this.serviceUrl}addFeedLogs`, feedlogs, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   * - Prevents completion callbacks for cancelled requests
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.batchTransactionsService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
